// src/oura/components/newscan/RecipeChangeCard.tsx
//
// The recipe update, as the first decision on the Review step of a
// recipe_change draft: "The new recipe changes 9 of these 50", and three ways
// out. Nothing is rewritten until staff pick one.
//
// - Take all 9 (blue, recommended): every changed question takes the new
//   wording. The card goes; the outcome line says so, with Undo.
// - Choose each: the card lists only the changed questions, the old wording
//   above the new, with Keep / Take on each. The wording not picked is struck
//   through and grey. Under each, the old wording's result in the last scan,
//   per engine (its place in the list, or "Not named"), and above the list
//   "Keep the ones it ranks on" keeps every question an engine ranked. A question whose
//   group changed has no choice ("Follows the new recipe"). The count of each
//   ("7 take · 2 keep") and "Use these" go to the foot through `onChoosing`;
//   without it they sit at the foot of the card.
// - Keep all: nothing changes, so there is nothing to review or scan. It
//   leaves the flow for the business page.
//
// Renders nothing once the recipe is settled (the decision is gone); the
// decision list then moves focus to the next open card.
//
// Focus: Choose each moves into the list (the first row's checked option);
// Cancel puts it back on Choose each.

import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import type { ScanDraftDecision, ScanDraftLastResult, ScanDraftRecipeRow } from "@service/aeo/scanDraftTypes";
import { cn } from "@oura/lib/cn";
import { EngineLogo, PillButton, RankChipEmpty, SURFACE, Tag, TYPE } from "@oura/ui";
import { useScanFlow } from "@oura/newscan/ScanFlowContext";
import type { FootAction } from "@oura/newscan/flowSteps";
import { DecisionCard, type DecisionCardAction } from "./DecisionCard";
import { Muted } from "./Muted";
import { RadioSeg } from "./RadioSeg";
import { useWritesPaused } from "@context/maintenance";

const RECIPE = {
    keptNotice: "Kept the current 50.",
    takeAll: (k: number) => `Take all ${k}`,
    chooseEach: "Choose each",
    keepAll: "Keep all",
    cancel: "Cancel",
    useThese: "Use these",
    tally: (take: number, keep: number) => `${take} take · ${keep} keep`,
    keep: "Keep",
    take: "Take",
    forced: "Follows the new recipe",
    kept: "Kept",
    replaced: "Replaced",
    pickLabel: (n: number) => `Question ${n}`,
    listLabel: "Changed questions",
    chooseHint: "Keep the old wording or take the new one on each.",
    keepRanked: "Keep the ones it ranks on",
    lastLabel: "Last scan",
    rank: (place: number) => `#${place}`,
    named: "Named",
    notNamed: "Not named",
    notNamedWhy: (engine: string) => `${engine} did not name the business in the last scan`,
    engineSaid: (engine: string, what: string) => `${engine}: ${what}`,
} as const;

const ENGINE: Record<ScanDraftLastResult["engine"], { logo: "chatgpt" | "gemini" | "ai_overview"; name: string }> = {
    chatgpt: { logo: "chatgpt", name: "ChatGPT" },
    gemini: { logo: "gemini", name: "Gemini" },
    google_ai: { logo: "ai_overview", name: "Google AI" },
};

/** Ranked by any engine in the last scan. */
const ranks = (r: ScanDraftRecipeRow) => (r.last ?? []).some((x) => x.rank !== null && x.rank > 0);

/** The old wording's result in the last scan, one engine after another. Nothing when not measured. */
function LastResult({ last }: { last: ScanDraftLastResult[] | null | undefined }) {
    if (!last || last.length === 0) return null;
    return (
        <span className={cn(TYPE.tm, "mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-gray-500")}>
            <span>{RECIPE.lastLabel}</span>
            {last.map((x) => {
                const e = ENGINE[x.engine];
                const place = x.named && typeof x.rank === "number" && x.rank > 0 ? x.rank : null;
                const what = place !== null ? RECIPE.rank(place) : x.named ? RECIPE.named : RECIPE.notNamed;
                return (
                    <span key={x.engine} className="inline-flex items-center gap-1">
                        <EngineLogo engine={e.logo} size={14} />
                        <span className="sr-only">{RECIPE.engineSaid(e.name, what)}</span>
                        <span aria-hidden className="inline-flex">
                            {place !== null ? (
                                <Tag tone="blue" className="tabular-nums">
                                    {what}
                                </Tag>
                            ) : x.named ? (
                                <Tag tone="gray">{what}</Tag>
                            ) : (
                                <RankChipEmpty label={what} why={RECIPE.notNamedWhy(e.name)} />
                            )}
                        </span>
                    </span>
                );
            })}
        </span>
    );
}

type Pick = "take" | "keep";

/** One wording: dark when it is the one picked, struck through and grey when not. */
function Wording({ kept, label, className, children }: { kept: boolean; label: string; className?: string; children: string }) {
    const body = (
        <>
            <span className="sr-only">{label}: </span>
            {children}
        </>
    );
    return kept ? (
        <span className={cn(TYPE.tb, "block break-words text-gray-900", className)}>{body}</span>
    ) : (
        <s className={cn(TYPE.tc, "block break-words text-gray-400", className)}>{body}</s>
    );
}

/** What the foot shows while staff choose each. */
export interface RecipeChooseFoot {
    /** "7 take · 2 keep". */
    status: string;
    /** "Use these". */
    primary: FootAction;
    /** Leave choose-each, back to the three options (the foot's Back can call it). */
    cancel(): void;
}

export interface RecipeChangeCardProps {
    /** The open recipe_change decision. Omit to read it from the draft. */
    decision?: ScanDraftDecision | null;
    /**
     * While choose-each is open: the tally and "Use these" for the foot. Null
     * when it closes. Without it, both sit at the foot of the card.
     */
    onChoosing?: (foot: RecipeChooseFoot | null) => void;
    className?: string;
}

export function RecipeChangeCard({ decision: given, onChoosing, className }: RecipeChangeCardProps) {
    const { ctl, close } = useScanFlow();
    const writesPaused = useWritesPaused();
    const draft = ctl.draft;
    const decision =
        given ?? draft?.decisions.find((d) => d.type === "recipe_change" && d.state === "open") ?? null;
    const change = draft?.recipeChange ?? null;
    const rows = useMemo(() => (change?.rows ?? []).filter((r) => !r.same), [change]);

    const [choosing, setChoosing] = useState(false);
    const [picks, setPicks] = useState<Record<number, Pick>>({});
    const [pending, setPending] = useState<"take_all" | "keep_all" | "choose_each" | null>(null);
    const [error, setError] = useState<string | null>(null);
    const listId = useId();
    const rootRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLUListElement>(null);

    const pickOf = (r: ScanDraftRecipeRow): Pick => (r.forced ? "take" : picks[r.n] ?? r.pick ?? "take");
    const keepCount = rows.filter((r) => pickOf(r) === "keep").length;
    const takeCount = rows.length - keepCount;
    // The local pending counts too: once it is set, the sibling choices stay shut until this one settles.
    const busy = ctl.busy !== null || pending !== null;
    const runningRef = useRef(false);
    const open = Boolean(decision && change && change.mode === null && rows.length > 0);

    async function run(mode: "take_all" | "keep_all" | "choose_each") {
        // A second click can land before React has re-rendered the disabled state.
        if (runningRef.current || ctl.busy !== null || writesPaused) return;
        runningRef.current = true;
        setError(null);
        setPending(mode);
        try {
            const sendPicks =
                mode === "choose_each" ? Object.fromEntries(rows.filter((r) => !r.forced).map((r) => [r.n, pickOf(r)])) : undefined;
            const failure = await ctl.setRecipe(mode, sendPicks);
            if (failure) {
                if (failure.kind !== "stale") setError(failure.message);
                return;
            }
            setChoosing(false);
            // Keeping all leaves nothing to review or scan: back to the business.
            if (mode === "keep_all") close({ notice: RECIPE.keptNotice });
        } finally {
            runningRef.current = false;
            setPending(null);
        }
    }

    // Held in a ref so the foot's "Use these" stays one function and still sends the latest picks.
    const runRef = useRef(run);
    runRef.current = run;
    const submitChoices = useCallback(() => void runRef.current("choose_each"), []);

    const startChoosing = () => {
        setError(null);
        setChoosing(true);
        // The pressed button is gone: into the list, at the first row's current option.
        requestAnimationFrame(() => {
            const list = listRef.current;
            (list?.querySelector<HTMLElement>('[role="radio"][aria-checked="true"]') ?? list)?.focus({ preventScroll: true });
        });
    };
    const cancelChoosing = useCallback(() => {
        setChoosing(false);
        // Back on Choose each, the button that opened the list.
        requestAnimationFrame(() => {
            const buttons = rootRef.current?.querySelectorAll<HTMLButtonElement>("button") ?? [];
            Array.from(buttons)
                .find((b) => b.textContent?.trim() === RECIPE.chooseEach)
                ?.focus({ preventScroll: true });
        });
    }, []);

    // The foot carries the tally and "Use these" while choosing.
    const footRef = useRef(onChoosing);
    footRef.current = onChoosing;
    useEffect(() => {
        const report = footRef.current;
        if (!report) return;
        if (!(open && choosing)) {
            report(null);
            return;
        }
        report({
            status: RECIPE.tally(takeCount, keepCount),
            primary: { label: RECIPE.useThese, onClick: submitChoices, busy: pending === "choose_each", disabled: (busy && pending !== "choose_each") || writesPaused },
            cancel: cancelChoosing,
        });
    }, [open, choosing, takeCount, keepCount, pending, busy, submitChoices, cancelChoosing, writesPaused]);
    useEffect(() => () => footRef.current?.(null), []);

    if (!open || !decision || !change) return null;

    const k = rows.length;
    const footInCard = !onChoosing;
    const actions: DecisionCardAction[] = choosing
        ? [
              ...(footInCard
                  ? [
                        {
                            key: "use",
                            label: RECIPE.useThese,
                            kind: "primary" as const,
                            busy: pending === "choose_each",
                            disabled: writesPaused,
                            onClick: submitChoices,
                        },
                    ]
                  : []),
              { key: "cancel", label: RECIPE.cancel, kind: "text" as const, disabled: pending !== null, onClick: cancelChoosing },
          ]
        : [
              {
                  key: "take",
                  label: decision.recommended?.label ?? RECIPE.takeAll(k),
                  kind: "primary",
                  busy: pending === "take_all",
                  disabled: writesPaused,
                  onClick: () => void run("take_all"),
              },
              {
                  key: "choose",
                  label: RECIPE.chooseEach,
                  kind: "secondary",
                  onClick: startChoosing,
              },
              { key: "keep", label: RECIPE.keepAll, kind: "secondary", busy: pending === "keep_all", disabled: writesPaused, onClick: () => void run("keep_all") },
          ];

    return (
        <div ref={rootRef} className="contents">
            <DecisionCard
                severity="decide"
                title={decision.title}
                detail={decision.detail}
                actions={actions}
                disabled={busy}
                className={className}
            >
                {choosing && (
                    <div className="space-y-3">
                        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                            <Muted as="p">{RECIPE.chooseHint}</Muted>
                            {rows.some((r) => !r.forced && ranks(r)) && (
                                <PillButton
                                    variant="text"
                                    disabled={pending !== null}
                                    onClick={() =>
                                        setPicks((was) => {
                                            const next = { ...was };
                                            rows.forEach((r) => {
                                                if (!r.forced && ranks(r)) next[r.n] = "keep";
                                            });
                                            return next;
                                        })
                                    }
                                >
                                    {RECIPE.keepRanked}
                                </PillButton>
                            )}
                        </div>
                        <ul
                            ref={listRef}
                            role="list"
                            aria-label={RECIPE.listLabel}
                            id={listId}
                            tabIndex={-1}
                            className="list-plain m-0 space-y-2 p-0 outline-none"
                        >
                            {rows.map((r) => (
                                <li
                                    key={r.n}
                                    className={cn(SURFACE.inner, "flex flex-col gap-2.5 sm:flex-row sm:items-center sm:gap-4")}
                                >
                                    <span className="flex min-w-0 flex-1 items-start gap-3">
                                        <span className={cn(TYPE.tc, "w-6 shrink-0 pt-px text-right tabular-nums text-gray-400")}>{r.n}</span>
                                        <span className="min-w-0 flex-1">
                                            <Wording kept={pickOf(r) === "keep"} label={pickOf(r) === "keep" ? RECIPE.kept : RECIPE.replaced}>
                                                {r.current}
                                            </Wording>
                                            <LastResult last={r.last} />
                                            <Wording kept={pickOf(r) === "take"} label={pickOf(r) === "take" ? RECIPE.kept : RECIPE.replaced} className="mt-1">
                                                {r.proposed}
                                            </Wording>
                                        </span>
                                    </span>
                                    <span className="shrink-0 pl-9 sm:pl-0">
                                        {r.forced ? (
                                            <Muted>{RECIPE.forced}</Muted>
                                        ) : (
                                            <RadioSeg<Pick>
                                                size="sm"
                                                ariaLabel={RECIPE.pickLabel(r.n)}
                                                options={[
                                                    { key: "keep", label: RECIPE.keep },
                                                    { key: "take", label: RECIPE.take },
                                                ]}
                                                value={pickOf(r)}
                                                disabled={pending !== null}
                                                onChange={(v) => setPicks((was) => ({ ...was, [r.n]: v }))}
                                            />
                                        )}
                                    </span>
                                </li>
                            ))}
                        </ul>
                        {footInCard && (
                            <p className={cn(TYPE.tc, "m-0 tabular-nums text-gray-500")} aria-live="polite">
                                {RECIPE.tally(takeCount, keepCount)}
                            </p>
                        )}
                    </div>
                )}
                {error && (
                    <p role="alert" className={cn(TYPE.tc, "m-0 mt-3 font-medium text-rose-600")}>
                        {error}
                    </p>
                )}
            </DecisionCard>
        </div>
    );
}

export default RecipeChangeCard;
